import React, { useState, useEffect } from 'react';
import { Eye, Check, Mail, Phone, Calendar, Search, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminJobApplications() {
  const { t, getLocalizedContent } = useLanguage();
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchItems(); fetchJobs(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/job-applications`);
      setItems(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs?active_only=false`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/job-applications/${id}`, { status });
      setItems(items.map(i => i.id === id ? { ...i, status } : i));
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getJobTitle = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? getLocalizedContent(job.title) : 'Poste inconnu';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      reviewed: 'bg-blue-100 text-blue-700',
      interview: 'bg-purple-100 text-purple-700',
      hired: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const filteredItems = filter === 'all' ? items : items.filter(i => i.status === filter);

  return (
    <div data-testid="admin-job-applications">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{t('admin_applications')}</h1>
      </div>

      <div className="flex gap-4 mb-6">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="reviewed">Examiné</SelectItem>
            <SelectItem value="interview">Entretien</SelectItem>
            <SelectItem value="hired">Embauché</SelectItem>
            <SelectItem value="rejected">Rejeté</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div></div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-20 text-gray-500">Aucune candidature</div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item) => (
            <Card key={item.id} className="border-0 shadow-sm cursor-pointer hover:shadow-md" onClick={() => setSelectedItem(item)}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-[#0F172A]">{item.name}</h3>
                    <Badge className={getStatusColor(item.status)}>{item.status}</Badge>
                  </div>
                  <p className="text-sm text-gray-600">{getJobTitle(item.job_id)}</p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(item.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Candidature de {selectedItem?.name}</DialogTitle></DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F8F9FA] rounded-lg">
                <p className="font-medium">{getJobTitle(selectedItem.job_id)}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-sm text-gray-500">Email</span><p className="font-medium">{selectedItem.email}</p></div>
                <div><span className="text-sm text-gray-500">Téléphone</span><p className="font-medium">{selectedItem.phone}</p></div>
              </div>
              {selectedItem.cover_letter && (
                <div><span className="text-sm text-gray-500">Lettre de motivation</span><p className="whitespace-pre-wrap text-sm">{selectedItem.cover_letter}</p></div>
              )}
              <div>
                <span className="text-sm text-gray-500">Statut</span>
                <Select value={selectedItem.status} onValueChange={(v) => { updateStatus(selectedItem.id, v); setSelectedItem({ ...selectedItem, status: v }); }}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="reviewed">Examiné</SelectItem>
                    <SelectItem value="interview">Entretien</SelectItem>
                    <SelectItem value="hired">Embauché</SelectItem>
                    <SelectItem value="rejected">Rejeté</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <a href={`mailto:${selectedItem.email}`}><Button className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white"><Mail className="h-4 w-4 mr-2" />Email</Button></a>
                {selectedItem.phone && <a href={`tel:${selectedItem.phone}`}><Button variant="outline"><Phone className="h-4 w-4 mr-2" />Appeler</Button></a>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
